import React, { useEffect, useState } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const FEATUREBASE_ORGANIZATION = import.meta.env.VITE_FEATUREBASE_ORGANIZATION;

export const FeaturebaseSSO: React.FC = () => {
  const [status, setStatus] = useState<'checking' | 'redirecting' | 'error'>('checking');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const handleSSO = async () => {
      try {
        // Récupérer le paramètre return_to depuis l'URL
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get('return_to') || 'https://' + FEATUREBASE_ORGANIZATION + '.featurebase.app';

        // Vérifier si l'utilisateur est authentifié
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !sessionData?.session) {
          // Utilisateur non authentifié : rediriger vers login avec préservation du return_to
          const currentUrl = window.location.href;
          const loginUrl = `/login?return_to=${encodeURIComponent(currentUrl)}`;
          window.location.href = loginUrl;
          return;
        }

        const session = sessionData.session;

        // Vérifier que l'organisation Featurebase est configurée
        if (!FEATUREBASE_ORGANIZATION) {
          throw new Error('Configuration Featurebase manquante (VITE_FEATUREBASE_ORGANIZATION)');
        }

        // Générer le JWT via l'Edge Function
        setStatus('redirecting');
        console.log('[Featurebase SSO] Calling featurebase-jwt Edge Function...');
        
        const { data, error: jwtError } = await supabase.functions.invoke('featurebase-jwt');

        if (jwtError) {
          console.error('[Featurebase SSO] JWT generation error:', jwtError);
          
          // Si l'erreur est liée à l'authentification, rediriger vers login
          if (jwtError.message?.includes('Unauthorized') || jwtError.message?.includes('401')) {
            const currentUrl = window.location.href;
            const loginUrl = `/login?return_to=${encodeURIComponent(currentUrl)}`;
            window.location.href = loginUrl;
            return;
          }
          
          throw new Error(`Erreur lors de la génération du JWT: ${jwtError.message || 'Erreur inconnue'}`);
        }

        const jwt = data?.token;
        if (!jwt || typeof jwt !== 'string') {
          throw new Error('JWT invalide reçu de l\'Edge Function');
        }

        console.log('[Featurebase SSO] JWT generated successfully, redirecting to Featurebase...');

        // Construire l'URL de redirection vers Featurebase
        const featurebaseUrl = `https://${FEATUREBASE_ORGANIZATION}.featurebase.app/api/v1/auth/access/jwt?jwt=${encodeURIComponent(jwt)}&return_to=${encodeURIComponent(returnTo)}`;
        
        // Rediriger vers Featurebase
        window.location.href = featurebaseUrl;
      } catch (err: any) {
        console.error('[Featurebase SSO] Error:', err);
        setError(err.message || 'Une erreur est survenue lors de l\'authentification SSO');
        setStatus('error');
      }
    };

    handleSSO();
  }, []);

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center justify-center mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-4">
            Erreur d'authentification
          </h2>
          <p className="text-slate-600 text-center mb-6">
            {error}
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-lg transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <div className="flex items-center justify-center mb-6">
          <Loader className="w-12 h-12 text-amber-500 animate-spin" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">
          {status === 'checking' ? 'Vérification de l\'authentification...' : 'Redirection vers Featurebase...'}
        </h2>
        <p className="text-slate-600">
          {status === 'checking' 
            ? 'Vérification de votre session en cours...'
            : 'Génération du token et redirection...'}
        </p>
      </div>
    </div>
  );
};
